import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  X, 
  AlertCircle, 
  CheckCircle,
  Calendar,
  Users,
  Vote,
  Clock
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { CreateElectionRequest, UpdateElectionRequest, ElectionPosition } from '@/types';

const CreateElectionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [positions, setPositions] = useState<ElectionPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState<CreateElectionRequest>({
    title: '',
    description: '',
    position: '',
    position_description: '',
    start_date: '',
    end_date: '',
    is_secret_ballot: true,
    allow_abstain: true,
    max_candidates: 10,
    min_candidates: 1,
    eligibility_criteria: {},
    voting_rules: {}
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = Boolean(id);

  useEffect(() => {
    fetchPositions();
    if (isEdit) {
      fetchElection();
    }
  }, [isEdit, id]);

  const fetchPositions = async () => {
    try {
      const response = await fetch('/api/v1/elections/positions/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPositions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  const fetchElection = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/elections/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const election = data.data;
        setFormData({
          title: election.title,
          description: election.description || '',
          position: election.position,
          position_description: election.position_description || '',
          start_date: election.start_date.split('T')[0] + 'T' + election.start_date.split('T')[1].substring(0, 5),
          end_date: election.end_date.split('T')[0] + 'T' + election.end_date.split('T')[1].substring(0, 5),
          is_secret_ballot: election.is_secret_ballot,
          allow_abstain: election.allow_abstain,
          max_candidates: election.max_candidates,
          min_candidates: election.min_candidates,
          eligibility_criteria: election.eligibility_criteria || {},
          voting_rules: election.voting_rules || {}
        });
      }
    } catch (error) {
      console.error('Error fetching election:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.position.trim()) {
      newErrors.position = 'Position is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      
      if (endDate <= startDate) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    if (formData.max_candidates < formData.min_candidates) {
      newErrors.max_candidates = 'Max candidates must be greater than or equal to min candidates';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CreateElectionRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const url = isEdit ? `/api/v1/elections/${id}` : '/api/v1/elections';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitSuccess(true);
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          position: '',
          position_description: '',
          start_date: '',
          end_date: '',
          is_secret_ballot: true,
          allow_abstain: true,
          max_candidates: 10,
          min_candidates: 1,
          eligibility_criteria: {},
          voting_rules: {}
        });
        
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/app/elections');
        }, 2000);
      } else {
        const errorData = await response.json();
        setErrors({
          general: errorData.message || 'Failed to save election. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error saving election:', error);
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isEdit ? 'Election Updated Successfully!' : 'Election Created Successfully!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isEdit 
              ? 'The election has been updated and changes are now live.'
              : 'The election has been created and is ready for candidate registration.'
            }
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/app/elections')}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              View All Elections
            </button>
            <button
              onClick={() => {
                setSubmitSuccess(false);
                if (!isEdit) {
                  setFormData({
                    title: '',
                    description: '',
                    position: '',
                    position_description: '',
                    start_date: '',
                    end_date: '',
                    is_secret_ballot: true,
                    allow_abstain: true,
                    max_candidates: 10,
                    min_candidates: 1,
                    eligibility_criteria: {},
                    voting_rules: {}
                  });
                }
              }}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              {isEdit ? 'Edit Another' : 'Create Another'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/app/elections')}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Elections
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Election' : 'Create New Election'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEdit 
              ? 'Update election details and settings.'
              : 'Set up a new election for RWA committee positions.'
            }
          </p>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Election Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 2024 RWA Committee Elections"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position *
                </label>
                <select
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.position ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a position</option>
                  {positions.map((position) => (
                    <option key={position.id} value={position.name}>
                      {position.name}
                    </option>
                  ))}
                </select>
                {errors.position && (
                  <p className="mt-1 text-sm text-red-600">{errors.position}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the election..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position Description
              </label>
              <textarea
                value={formData.position_description}
                onChange={(e) => handleInputChange('position_description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Detailed description of the position and responsibilities..."
              />
            </div>

            {/* Voting Period */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.start_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.end_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.end_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
                )}
              </div>
            </div>

            {/* Candidate Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline h-4 w-4 mr-1" />
                  Minimum Candidates
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.min_candidates}
                  onChange={(e) => handleInputChange('min_candidates', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline h-4 w-4 mr-1" />
                  Maximum Candidates
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_candidates}
                  onChange={(e) => handleInputChange('max_candidates', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.max_candidates ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.max_candidates && (
                  <p className="mt-1 text-sm text-red-600">{errors.max_candidates}</p>
                )}
              </div>
            </div>

            {/* Voting Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Voting Settings</h3>
              
              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_secret_ballot}
                    onChange={(e) => handleInputChange('is_secret_ballot', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Secret Ballot</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allow_abstain}
                    onChange={(e) => handleInputChange('allow_abstain', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Allow Abstain</span>
                </label>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/app/elections')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEdit ? 'Update Election' : 'Create Election'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateElectionPage;
